import { RegistrationFlow, UpdateRegistrationFlowBody } from "@ory/client";
import { UserAuthCard } from "@ory/elements";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { routes } from "@routes";
import { ory } from "@services/authentication";
import { ErrorData, handleError } from "@pages/authentication/error";
import { Authentication } from "@pages/authentication/Authentication";
import { Spinner } from "@thetreep/ui";
import { useTranslation } from "react-i18next";
import { translateFlow } from "./translations";

export function Registration({ onSwitch }: { onSwitch?: () => void }) {
  const { t } = useTranslation();
  const [flow, setFlow] = useState<RegistrationFlow | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const returnTo = window.location.origin;

  const errorHandler: (error: ErrorData) => unknown = handleError(
    (flowId: string) => getFlow(flowId),
    setFlow,
    routes.ERROR,
    true,
    navigate,
  );

  const getFlow = async (flowId: string) => {
    try {
      const res = await ory().getRegistrationFlow({ id: flowId });
      setFlow(translateFlow(t, res.data));
    } catch (e) {
      errorHandler(e as ErrorData);
    }
  };

  const createFlow = async () => {
    try {
      const loginChallenge = searchParams.get("login_challenge");
      const res = await ory().createBrowserRegistrationFlow({
        ...(returnTo && { returnTo }),
        ...(loginChallenge && { loginChallenge }),
      });

      if (!onSwitch) {
        setSearchParams({ flow: res.data.id });
      }
      setFlow(translateFlow(t, res.data));
    } catch (error) {
      errorHandler(error as ErrorData);
    }
  };

  const submitFlow = async (body: UpdateRegistrationFlowBody) => {
    if (!flow) {
      navigate(routes.REGISTRATION, { replace: true });
      return;
    }

    try {
      const res = await ory().updateRegistrationFlow({
        flow: flow.id,
        updateRegistrationFlowBody: body,
      });
      const data = res.data;
      if ("continue_with" in data) {
        for (const cw of data.continue_with ?? []) {
          if (cw.action === "show_verification_ui") {
            const search = new URLSearchParams();
            search.set("flow", cw.flow.id);
            navigate(
              {
                pathname: routes.VERIFICATION,
                search: search.toString(),
              },
              { replace: true },
            );
            return;
          }
        }
      }
      navigate(0);
    } catch (error) {
      errorHandler(error as ErrorData);
    }
  };

  useEffect(() => {
    const flowId = onSwitch ? null : searchParams.get("flow");
    if (flowId) {
      getFlow(flowId);
      return;
    }
    // oxlint-disable-next-line react-hooks/set-state-in-effect
    createFlow();
  }, []);

  if (!flow) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <Authentication>
      <UserAuthCard
        className="authCard"
        flowType="registration"
        flow={flow}
        additionalProps={{
          loginURL: {
            handler: () => {
              const search = new URLSearchParams();
              if (flow.return_to) {
                search.set("return_to", flow.return_to);
              }
              if (flow.oauth2_login_challenge) {
                search.set("login_challenge", flow.oauth2_login_challenge);
              }
              if (onSwitch) {
                onSwitch();
              } else {
                navigate(
                  { pathname: routes.LOGIN, search: search.toString() },
                  { replace: true },
                );
              }
            },
          },
        }}
        includeScripts={true}
        onSubmit={({ body }) => submitFlow(body as UpdateRegistrationFlowBody)}
      />
    </Authentication>
  );
}
